import React, { useState } from 'react';
import { Book } from '../types';
import { ExternalLink, ChevronRight, ChevronDown, ChevronUp, ChevronLeft } from 'lucide-react';
import { useTheme, getThemeClasses } from '../context/ThemeContext';

interface BookCardProps {
  book: Book;
  featured?: boolean;
  onNextBook?: () => void;
  onPreviousBook?: () => void;
  showFeaturedLabel?: boolean;
  showNavigation?: boolean;
  isFirstBook?: boolean;
  isLastBook?: boolean;
}

export default function BookCard({ 
  book, 
  featured = false, 
  onNextBook, 
  onPreviousBook,
  showFeaturedLabel = false,
  showNavigation = false,
  isFirstBook = false,
  isLastBook = false
}: BookCardProps) {
  const { theme } = useTheme();
  const themeClasses = getThemeClasses(theme);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`${themeClasses.card} rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg
                    ${featured ? 'sm:flex' : 'max-w-2xl mx-auto'}`}>
      <div className={`relative ${featured ? 'sm:w-1/4' : 'w-full max-w-[180px] mx-auto'}`}>
        <a
          href={book.amazonLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block cursor-pointer transition-opacity duration-200 hover:opacity-90"
          aria-label={`View ${book.title} on Amazon`}
        >
          <img
            src={book.coverUrl}
            alt={book.title}
            className={`w-full object-contain ${featured ? 'sm:h-full h-44' : 'h-[260px]'}`}
            loading="lazy"
          />
        </a>
        {featured && showFeaturedLabel && (
          <div className="absolute top-2 left-2">
            <div className={`px-2 py-1 ${themeClasses.primary} text-white text-xs font-semibold rounded-full`}>
              Featured
            </div>
          </div>
        )}
      </div>
      <div className={`p-3 sm:p-4 ${featured ? 'sm:w-3/4' : ''}`}>
        <div className="flex flex-col h-full">
          <div>
            <h3 className={`text-lg sm:text-xl font-bold ${themeClasses.text} mb-1`}>{book.title}</h3>
            <p className={`text-sm sm:text-base ${themeClasses.textSecondary} mb-2`}>by {book.author}</p>
            
            <div className="flex flex-wrap gap-1 mb-2">
              {book.genre.map((genre, index) => (
                <span
                  key={index}
                  className={`px-2 py-0.5 ${themeClasses.highlight} text-xs font-medium rounded-full`}
                >
                  {genre}
                </span>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-1 mb-2">
              {book.mood.map((mood, index) => (
                <span
                  key={index}
                  className={`px-2 py-0.5 ${themeClasses.secondary} ${themeClasses.text} text-xs font-medium rounded-full`}
                >
                  {mood}
                </span>
              ))}
            </div>
            
            <div className="mb-3">
              <p className={`text-xs sm:text-sm ${themeClasses.text} ${isExpanded ? '' : 'line-clamp-2'}`}>
                {book.summary}
              </p>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`mt-1 ${themeClasses.icon} hover:opacity-80 transition-colors text-xs sm:text-sm inline-flex items-center`}
              >
                {isExpanded ? (
                  <>
                    Show Less
                    <ChevronUp className="ml-1 h-3 w-3" />
                  </>
                ) : (
                  <>
                    Read More
                    <ChevronDown className="ml-1 h-3 w-3" />
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="mt-auto">
            <div className="flex flex-wrap gap-2 mb-2">
              <a
                href={book.amazonLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center px-3 py-1.5 ${themeClasses.primary} text-white rounded-lg
                         hover:${themeClasses.primaryHover} transition-colors duration-200 text-xs sm:text-sm font-medium`}
              >
                View on Amazon
                <ExternalLink className="ml-1.5 h-3 w-3 sm:h-4 sm:w-4" />
              </a>
            </div>

            {showNavigation && (
              <div className={`flex justify-between items-center mt-4 pt-4 border-t ${themeClasses.border}`}>
                <button
                  onClick={onPreviousBook}
                  disabled={isFirstBook}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium
                           ${isFirstBook 
                             ? `${themeClasses.secondary} text-gray-400 cursor-not-allowed` 
                             : `${themeClasses.secondary} ${themeClasses.text} hover:${themeClasses.secondaryHover} transition-colors duration-200`}`}
                >
                  <ChevronLeft className="mr-1.5 h-3 w-3 sm:h-4 sm:w-4" />
                  Previous Book
                </button>
                
                <button
                  onClick={onNextBook}
                  disabled={isLastBook}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium
                           ${isLastBook
                             ? `${themeClasses.secondary} text-gray-400 cursor-not-allowed`
                             : `${themeClasses.secondary} ${themeClasses.text} hover:${themeClasses.secondaryHover} transition-colors duration-200`}`}
                >
                  Next Book
                  <ChevronRight className="ml-1.5 h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}